import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Clock, MapPin, User, Users, Check, X, CalendarDays, ClipboardList, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", training_program_id: "", lesson_id: "", location_id: "", trainer_id: "", date: "", time: "", end_time: "", capacity: 8, status: "scheduled", description: "", visibility: "client_bookable" };

export default function Classes() {
  const { user } = useAuth();
  const ref = useRefData();
  const navigate = useNavigate();
  const canManage = ["super_admin", "head_office_admin", "franchise_owner", "location_manager"].includes(user?.role);
  const canStaff = user?.role !== "client";
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [manage, setManage] = useState(null); // class for enroll/attendance
  const [enrollDog, setEnrollDog] = useState("");
  const [saving, setSaving] = useState(false);
  const [formLessons, setFormLessons] = useState([]);

  const fetchLessons = async (pid) => { if (!pid) return setFormLessons([]); try { const r = await api.get(`/programs/${pid}/detail`); setFormLessons(r.data.lessons || []); } catch { setFormLessons([]); } };
  const pickProgram = (v) => { setForm((f) => ({ ...f, training_program_id: v, lesson_id: "" })); fetchLessons(v); };

  const load = async () => { const r = await api.get("/classes"); setClasses(r.data); if (manage) setManage(r.data.find((c) => c.id === manage.id) || null); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setForm(empty); setEditing(null); setFormLessons([]); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...Object.fromEntries(Object.keys(empty).map((k) => [k, c[k] ?? empty[k]])) }); setEditing(c); setOpen(true); fetchLessons(c.training_program_id); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity) || 0 };
      if (editing) await api.put(`/classes/${editing.id}`, payload);
      else await api.post("/classes", payload);
      toast.success(editing ? "Class updated" : "Class created");
      setOpen(false); await load();
    } catch (e) { toast.error("Save failed"); }
    setSaving(false);
  };

  const remove = async (c) => { if (!window.confirm(`Delete "${c.name}"?`)) return; await api.delete(`/classes/${c.id}`); toast.success("Deleted"); await load(); };

  const enroll = async () => {
    if (!enrollDog) return;
    await api.post(`/classes/${manage.id}/enroll`, { dog_id: enrollDog });
    setEnrollDog(""); toast.success("Dog enrolled"); await load();
  };

  const setAttendance = async (dogId, status) => {
    await api.post(`/classes/${manage.id}/attendance`, { dog_id: dogId, attendance_status: status, notes: "" });
    await load();
  };

  const dogName = (id) => ref.dogs.find((d) => d.id === id)?.name || "Dog";
  const grouped = classes.reduce((acc, c) => { (acc[c.date] = acc[c.date] || []).push(c); return acc; }, {});

  return (
    <div>
      <PageHeader
        title="Classes"
        subtitle="Schedule classes, enroll dogs and mark attendance."
        action={canManage && <Button onClick={openCreate} className="gap-2" data-testid="classes-add-btn"><Plus size={16} strokeWidth={1.5} /> New Class</Button>}
      />

      <Tabs defaultValue="list">
        <TabsList className="h-auto">
          <TabsTrigger value="list" data-testid="classes-tab-list">List</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="classes-tab-calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          <Card className="border-border overflow-hidden">
            <Table data-testid="classes-table">
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  {["Class", "Program", "Location", "Trainer", "Date", "Time", "Enrolled", "Status", ""].map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wide font-semibold">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/40" data-testid="class-row">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.program_name || "—"}</TableCell>
                    <TableCell>{c.location_name || "—"}</TableCell>
                    <TableCell>{c.trainer_name || "—"}</TableCell>
                    <TableCell>{fmtDate(c.date)}</TableCell>
                    <TableCell>{c.time}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${c.capacity ? Math.min(100, (c.enrolled_count / c.capacity) * 100) : 0}%` }} /></div>
                        <span className="text-xs whitespace-nowrap">{c.enrolled_count}/{c.capacity}{c.waitlist_count ? ` (+${c.waitlist_count})` : ""}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <StatusBadge value={c.effective_status || c.status} />
                        {c.homework_assigned && <ClipboardList size={13} className="text-primary" data-testid="class-hw-indicator" />}
                        {c.attendance_complete && <CheckCircle2 size={13} className="text-green-600" data-testid="class-att-indicator" />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/classes/${c.id}`)} data-testid="class-open-btn">Open</Button>
                        <Button variant="outline" size="sm" onClick={() => setManage(c)} data-testid="class-manage-btn">Manage</Button>
                        {canManage && <><Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid="class-edit-btn"><Pencil size={15} strokeWidth={1.5} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid="class-delete-btn"><Trash2 size={15} strokeWidth={1.5} className="text-destructive" /></Button></>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {classes.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">No classes scheduled.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <div className="space-y-5">
            {Object.keys(grouped).sort().map((date) => (
              <div key={date} data-testid="calendar-day">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays size={16} className="text-primary" strokeWidth={1.5} />
                  <span className="font-heading font-semibold">{fmtDate(date)}</span>
                  <span className="text-xs text-muted-foreground">({grouped[date].length} class{grouped[date].length > 1 ? "es" : ""})</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {grouped[date].map((c) => (
                    <Card key={c.id} className="border-border hover:shadow-sm transition-all cursor-pointer" onClick={() => setManage(c)}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="font-medium text-sm">{c.name}</div>
                          <StatusBadge value={c.status} />
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1.5"><Clock size={12} /> {c.time}</div>
                          <div className="flex items-center gap-1.5"><MapPin size={12} /> {c.location_name}</div>
                          <div className="flex items-center gap-1.5"><User size={12} /> {c.trainer_name}</div>
                          <div className="flex items-center gap-1.5"><Users size={12} /> {c.enrolled_count}/{c.capacity} enrolled</div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
            {classes.length === 0 && <p className="text-muted-foreground text-center py-12">No classes scheduled.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create / edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Class" : "New Class"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <F label="Class name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="class-field-name" /></F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Training program"><P value={form.training_program_id} onChange={pickProgram} options={ref.programs.map((p) => ({ value: p.id, label: p.name }))} testid="class-field-program" /></F>
              <F label="Lesson (optional)"><P value={form.lesson_id} onChange={(v) => setForm({ ...form, lesson_id: v })} options={formLessons.map((l) => ({ value: l.id, label: l.title }))} testid="class-field-lesson" /></F>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <F label="Location"><P value={form.location_id} onChange={(v) => setForm({ ...form, location_id: v })} options={ref.locations.map((l) => ({ value: l.id, label: l.name }))} testid="class-field-location" /></F>
              <F label="Trainer"><P value={form.trainer_id} onChange={(v) => setForm({ ...form, trainer_id: v })} options={ref.trainers.map((t) => ({ value: t.id, label: t.full_name }))} testid="class-field-trainer" /></F>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <F label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="class-field-date" /></F>
              <F label="Start"><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="class-field-time" /></F>
              <F label="End"><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="class-field-endtime" /></F>
              <F label="Capacity"><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="class-field-capacity" /></F>
            </div>
            <F label="Description"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="class-field-description" /></F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Status"><P value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={[{ value: "scheduled", label: "Scheduled" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]} testid="class-field-status" /></F>
              <F label="Visibility"><P value={form.visibility} onChange={(v) => setForm({ ...form, visibility: v })} options={[{ value: "client_bookable", label: "Client bookable" }, { value: "internal", label: "Internal only" }]} testid="class-field-visibility" /></F>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="class-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage: enroll + attendance */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {manage && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{manage.name}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">{fmtDate(manage.date)} · {manage.time} · {manage.location_name}</p>

              {canStaff && (
                <div className="flex gap-2 mt-2">
                  <Select value={enrollDog} onValueChange={setEnrollDog}>
                    <SelectTrigger data-testid="enroll-dog-select"><SelectValue placeholder="Select a dog to enroll" /></SelectTrigger>
                    <SelectContent>{ref.dogs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {d.breed}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={enroll} data-testid="enroll-btn">Enroll</Button>
                </div>
              )}

              <div className="mt-2">
                <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">Enrolled Dogs ({manage.enrolled_count})</div>
                <div className="space-y-2">
                  {manage.enrollments?.length ? manage.enrollments.map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="enrollment-row">
                      <span className="text-sm font-medium">{dogName(e.dog_id)}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge value={e.attendance_status} />
                        {canStaff && (
                          <>
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setAttendance(e.dog_id, "present")} data-testid="mark-present"><Check size={14} className="text-green-600" /></Button>
                            <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setAttendance(e.dog_id, "absent")} data-testid="mark-absent"><X size={14} className="text-destructive" /></Button>
                          </>
                        )}
                      </div>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No dogs enrolled yet.</p>}
                </div>
              </div>

              {canStaff && (
                <Button variant="outline" className="w-full gap-2 mt-2" data-testid="class-assign-homework"
                  onClick={() => navigate("/homework")}>
                  <ClipboardList size={16} strokeWidth={1.5} /> Assign homework after class
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (<div className="grid gap-1.5"><Label className="text-sm">{label}</Label>{children}</div>);
function P({ value, onChange, options, testid }) {
  return (
    <Select value={value ? String(value) : ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}
